import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode
} from 'react';
import { createPortal } from 'react-dom';
import './floatingCommentDialog.css';

const DEFAULT_WIDTH = 660;
const DEFAULT_HEIGHT = 320;
const MIN_WIDTH = 360;
const MIN_HEIGHT = 220;

export interface FloatingCommentDialogProps {
  ariaLabel: string;
  className?: string;
  children: ReactNode;
  /** Extra class on the dialog surface (e.g. jarvis-pdf-selection-comment). */
  dialogClassName?: string;
}

type DragMode = 'move' | 'resize' | null;

/**
 * Centered comment dialog portaled to document.body so emoji pickers and the dialog
 * sit above the editor header. Drag the title bar to move; resize from the SE corner.
 */
export function FloatingCommentDialog({
  ariaLabel,
  className,
  dialogClassName,
  children
}: FloatingCommentDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const modeRef = useRef<DragMode>(null);
  const startRef = useRef({ x: 0, y: 0, left: 0, top: 0, width: 0, height: 0 });
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [size, setSize] = useState({ width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT });
  const [viewport, setViewport] = useState({ width: 1200, height: 800 });

  useEffect(() => {
    const sync = () => setViewport({ width: window.innerWidth, height: window.innerHeight });
    sync();
    window.addEventListener('resize', sync);
    return () => window.removeEventListener('resize', sync);
  }, []);

  const onMovePointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    const el = dialogRef.current;
    if (!el) return;
    event.preventDefault();
    const rect = el.getBoundingClientRect();
    modeRef.current = 'move';
    startRef.current = {
      x: event.clientX,
      y: event.clientY,
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  }, []);

  const onResizePointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    const el = dialogRef.current;
    if (!el) return;
    event.preventDefault();
    event.stopPropagation();
    const rect = el.getBoundingClientRect();
    modeRef.current = 'resize';
    startRef.current = {
      x: event.clientX,
      y: event.clientY,
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  }, []);

  const onPointerMove = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const mode = modeRef.current;
    if (!mode) return;
    const start = startRef.current;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;

    if (mode === 'move') {
      const maxX = Math.max(0, window.innerWidth - start.width);
      const maxY = Math.max(0, window.innerHeight - start.height);
      const left = Math.min(maxX, Math.max(0, start.left + dx));
      const top = Math.min(maxY, Math.max(0, start.top + dy));
      // Convert viewport coords to offset from centered default.
      const centerLeft = (window.innerWidth - start.width) / 2;
      const centerTop = (window.innerHeight - start.height) / 2;
      setOffset({ x: left - centerLeft, y: top - centerTop });
      return;
    }

    const width = Math.min(
      window.innerWidth - 24,
      Math.max(MIN_WIDTH, start.width + dx)
    );
    const height = Math.min(
      window.innerHeight - 24,
      Math.max(MIN_HEIGHT, start.height + dy)
    );
    setSize({ width, height });
  }, []);

  const onPointerUp = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (!modeRef.current) return;
    modeRef.current = null;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      /* ignore */
    }
  }, []);

  const maxWidth = Math.min(size.width, viewport.width - 24);
  const maxHeight = Math.min(size.height, viewport.height - 24);

  return createPortal(
    <div className={`floating-comment-dialog-root ${className ?? ''}`.trim()} role="presentation">
      <div
        ref={dialogRef}
        className={`floating-comment-dialog ${dialogClassName ?? ''}`.trim()}
        role="dialog"
        aria-label={ariaLabel}
        style={{
          width: maxWidth,
          height: maxHeight,
          transform: `translate(${offset.x}px, ${offset.y}px)`
        }}
        onKeyDownCapture={(e) => e.stopPropagation()}
        onKeyUpCapture={(e) => e.stopPropagation()}
      >
        <div
          className="floating-comment-dialog__drag"
          onPointerDown={onMovePointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          title="Drag to move"
        >
          <span className="floating-comment-dialog__drag-label">Comment</span>
          <span className="floating-comment-dialog__drag-hint">Drag to move</span>
        </div>
        <div className="floating-comment-dialog__body">{children}</div>
        <div
          className="floating-comment-dialog__resize"
          onPointerDown={onResizePointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          title="Drag to resize"
          aria-label="Resize comment box"
        />
      </div>
    </div>,
    document.body
  );
}
